import React, { useContext, useEffect, useRef, useState } from "react";
import WBCDataTable from "../data-table/WBCDataTable";
import PlayerLinkFormatter from "../table/PlayerLinkFormatter";
import EmailNotificationsContext from "../../contexts/EmailNotificationsContext";
import styled from "styled-components";
import WBCCheckbox from "../elements/WBCCheckbox";
import PropTypes from "prop-types";
import RosterStatusFormatter from "../mlb-players/mlb-player-summary/MLBPlayerRosterStatusFormatter";
import AuthContext from "../../contexts/AuthContext";
import { sortPlayers } from "../roster/RosterSortHelper";
import TableSortFilterContext from "../../contexts/TableSortFilterContext";
import PreferredNameFormatter from "../table/PreferredNameFormatter";
import NotificationNameFormatter from "./NotificationNameFormatter";

const CheckboxContainer = styled.div`
  cursor: pointer;
`;

const EmailNotificationsTableContainer = styled.div`
  height: 100%;
  position: relative;
`;

const OverlayCheckboxContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 10px;
  z-index: 20;
`;

const EmailNotificationsTable = () => {
  const ROW_HEIGHT = 45;
  const HEADER_ROW_HEIGHT = 40;

  const tableContainerRef = useRef(null);

  const tableSortFilterContext = useContext(TableSortFilterContext);
  const { emailIdsSelected, setEmailIdsSelected, filteredEmails, sortFilters, setSortFilters } = useContext(
    EmailNotificationsContext
  );
  const { isWBCIadmin } = useContext(AuthContext).state;

  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [sortedRows, setSortedRows] = useState([]);
  const [tableHeight, setTableHeight] = useState(400);
  const [isAllSelected, setIsAllSelected] = useState(false);

  useEffect(() => {
    const { col, dir } = sortFilters;
    setSortedRows(sortPlayers(col, dir, [...filteredEmails], false));
  }, [sortFilters, filteredEmails]);

  useEffect(() => {
    if (selectedIndexes.length === 0) {
      setEmailIdsSelected([]);
    }
  }, [selectedIndexes.length, setEmailIdsSelected]);

  useEffect(() => {
    if (emailIdsSelected.length === 0) {
      setSelectedIndexes([]);
    }
  }, [emailIdsSelected]);

  useEffect(() => {
    if (tableContainerRef.current) {
      const tableContainerHeight = tableContainerRef.current.offsetHeight;
      const maxRows = Math.floor(tableContainerHeight / ROW_HEIGHT);
      if (filteredEmails?.length > maxRows) {
        setTableHeight(maxRows * 45);
      } else {
        setTableHeight((filteredEmails?.length || 1) * ROW_HEIGHT + HEADER_ROW_HEIGHT + 25);
      }
    }
  }, [filteredEmails]);

  const DateFormatter = row => {
    return row.value ? new Date(row.value).toLocaleDateString() : "";
  };

  const handleSelectAll = () => {
    setIsAllSelected(!isAllSelected);

    if (!isAllSelected) {
      // Select all rows (when currently unselected)
      const allIndexes = sortedRows.map((_, index) => index);
      const allEmailIds = sortedRows.map(row => row.id);

      setSelectedIndexes(allIndexes);
      setEmailIdsSelected(allEmailIds);

      // Update isChecked property on all rows
      sortedRows.forEach(row => {
        row.isChecked = true;
      });
    } else {
      // Deselect all rows (when currently selected)
      setSelectedIndexes([]);
      setEmailIdsSelected([]);

      // Update isChecked property on all rows
      sortedRows.forEach(row => {
        row.isChecked = false;
      });
    }
  };

  const NotificationSelectCheckbox = ({ ...props }) => {
    return isWBCIadmin ? (
      <CheckboxContainer>
        <WBCCheckbox checked={props.row.isChecked} />
      </CheckboxContainer>
    ) : null;
  };

  NotificationSelectCheckbox.propTypes = {
    row: PropTypes.any,
    value: PropTypes.any,
    rowIdx: PropTypes.any,
    idx: PropTypes.any
  };

  const columns = [
    { name: "", key: "id", width: 50, formatter: NotificationSelectCheckbox, isSelected: true },
    { name: "Notification", key: "notificationName", width: 200, formatter: NotificationNameFormatter, sortable: true },
    { name: "Date Created", key: "createdTs", width: 180, formatter: DateFormatter, sortable: true },
    { name: "Sent to FED", key: "sentToFed", width: 150, formatter: DateFormatter, sortable: true },
    { name: "Sent to CLUB", key: "sentToClub", width: 150, formatter: DateFormatter, sortable: true },
    { name: "Last", key: "lastName", width: 180, formatter: PlayerLinkFormatter, sortable: true },
    { name: "First", key: "firstName", width: 180, formatter: PreferredNameFormatter, sortable: true },
    { name: "Pos", key: "position", width: 75, sortable: true },
    { name: "Federation", key: "federation", width: 150, sortable: true },
    { name: "Org", key: "org", width: 75, sortable: true },
    { name: "Club", key: "club", width: 180, sortable: true },
    { name: "MLB Roster Status", key: "rosterStatus", width: 200, formatter: RosterStatusFormatter, sortable: true },
    { name: "IL", key: "injuredListFlag", width: 180, sortable: true },
    { name: "40-man", key: "fortyMan", width: 180 },
    { name: "Tournament Type", key: "tournamentType", width: 180, sortable: true }
  ];

  return (
    <EmailNotificationsTableContainer ref={tableContainerRef}>
      <OverlayCheckboxContainer>
        <WBCCheckbox checked={isAllSelected} onClick={() => handleSelectAll()} />
      </OverlayCheckboxContainer>
      <WBCDataTable
        id="EmailNotificationsTable"
        useBCR
        rowKey="id"
        index
        columns={columns}
        headerRowHeight={HEADER_ROW_HEIGHT}
        height={tableHeight}
        rowHeight={ROW_HEIGHT}
        data={sortedRows}
        rowSelection={{
          showCheckbox: false,
          selectBy: {
            indexes: selectedIndexes
          }
        }}
        enableRowSelect={true}
        onRowClick={(rowIdx, row, column) => {
          if (column?.idx === 0 && isWBCIadmin) {
            if (selectedIndexes.includes(rowIdx)) {
              setSelectedIndexes(selectedIndexes.filter(i => i !== rowIdx));
              setEmailIdsSelected(emailIdsSelected.filter(id => id !== row.id));
              row.isChecked = false;
            } else {
              if (!emailIdsSelected.includes(row.id)) {
                setEmailIdsSelected(emailIdsSelected.concat(row.id));
              }
              setSelectedIndexes(selectedIndexes.concat(rowIdx));
              row.isChecked = true;
            }
          }
        }}
        sortFunc={(col, dir) => {
          setSortFilters({ col, dir });
          tableSortFilterContext.dispatch({
            type: "setSortFilters",
            sortFilters: { col, dir }
          });
        }}
        sortColumn={sortFilters.col}
        sortDirection={sortFilters.dir}
      />
    </EmailNotificationsTableContainer>
  );
};

export default EmailNotificationsTable;
